import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { iniciativasAPI, areasAPI, personasAPI } from '../services/api';

const CrearIniciativaInnovacion = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [areas, setAreas] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    nombre_de_la_oportunidad: '',
    tecnologia: '',
    tipo_de_iniciativa: 'iniciativa',
    descripcion: '',
    relevancia_para_negocio: '',
    probabilidad_de_aprobacion: 'media',
    madurez: 'poc',
    linea_estrategica: '',
    duracion_minima_estimada: 1,
    duracion_maxima_estimada: 12,
    fecha_de_inicio_planificada: '',
    fecha_de_fin_planificada: '',
    fecha_de_inicio_real: '',
    valor_minimo_estimado: 0,
    valor_maximo_estimado: 0,
    kpis_esperados: '',
    enlace_a_la_pagina_de_confluence: '',
    enlace_propuesta_pdf_sharepoint: '',
    innovacion: true,
    tipo_innovacion: 'innocard',
    estado: 'oportunidad_identificada',
    año: new Date().getFullYear(),
    fecha_de_entrega_propuesta_prevista: '',
    personas_asignadas: []
  });

  const [selectedPersona, setSelectedPersona] = useState('');
  const [selectedRol, setSelectedRol] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [areasResponse, personasResponse] = await Promise.all([
        areasAPI.getAll(),
        personasAPI.getAll()
      ]);
      
      // Handle different possible response structures
      const areasData = areasResponse.data?.results || areasResponse.data || [];
      const personasData = personasResponse.data?.results || personasResponse.data || [];
      
      setAreas(Array.isArray(areasData) ? areasData : []);
      setPersonas(Array.isArray(personasData) ? personasData : []);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Error cargando datos iniciales');
      // Set empty arrays as fallback
      setAreas([]);
      setPersonas([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePersonaChange = (personaId, role, checked) => {
    setFormData(prev => {
      const personas = [...prev.personas_asignadas];
      const existingIndex = personas.findIndex(p => p.persona === personaId && p.rol === role);
      
      if (checked && existingIndex === -1) {
        personas.push({ persona: personaId, rol: role });
      } else if (!checked && existingIndex !== -1) {
        personas.splice(existingIndex, 1);
      }
      
      return { ...prev, personas_asignadas: personas };
    });
  };

  const addPersonaToList = () => {
    if (!selectedPersona || !selectedRol) {
      setError('Debe seleccionar una persona y un rol');
      return;
    }

    const existingIndex = formData.personas_asignadas.findIndex(
      p => p.persona === parseInt(selectedPersona) && p.rol === selectedRol
    );

    if (existingIndex !== -1) {
      setError('Esta persona ya tiene asignado ese rol');
      return;
    }

    setFormData(prev => ({
      ...prev,
      personas_asignadas: [...prev.personas_asignadas, {
        persona: parseInt(selectedPersona),
        rol: selectedRol
      }]
    }));

    setSelectedPersona('');
    setSelectedRol('');
    setError(null);
  };

  const removePersonaFromList = (personaId, rol) => {
    setFormData(prev => ({
      ...prev,
      personas_asignadas: prev.personas_asignadas.filter(
        p => !(p.persona === personaId && p.rol === rol)
      )
    }));
  };

  const getPersonaById = (personaId) => {
    return personas.find(p => p.id === personaId) || {};
  };

  const getRolDisplay = (rol) => {
    const roles = {
      jefe_proyecto: 'Líder de Proyecto',
      lider_tecnico: 'Líder Técnico', 
      desarrollador: 'Desarrollador/Colaborador'
    };
    return roles[rol] || rol;
  };

  const validateForm = () => {
    if (!formData.nombre_de_la_oportunidad.trim()) {
      setError('El título es obligatorio');
      return false;
    }
    
    if (!formData.descripcion.trim()) {
      setError('El objetivo es obligatorio');
      return false;
    }

    // Solo validar fecha de entrega propuesta si no está vacía
    if (!formData.fecha_de_entrega_propuesta_prevista || formData.fecha_de_entrega_propuesta_prevista === '') {
      setError('La fecha de entrega propuesta es obligatoria');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Preparar datos para envío, limpiando fechas vacías
      const dataToSend = { ...formData };
      
      // Convertir fechas vacías a null
      const dateFields = [
        'fecha_de_inicio_planificada',
        'fecha_de_fin_planificada', 
        'fecha_de_inicio_real',
        'fecha_de_entrega_propuesta_prevista'
      ];
      
      dateFields.forEach(field => {
        if (dataToSend[field] === '') {
          dataToSend[field] = null;
        }
      });

      // Crear la iniciativa
      const response = await iniciativasAPI.create(dataToSend);
      const iniciativaId = response.data.id;

      // Asignar personas si hay alguna seleccionada
      for (const personaAsignada of formData.personas_asignadas) {
        try {
          await iniciativasAPI.agregarPersona(iniciativaId, {
            persona_id: personaAsignada.persona,
            rol: personaAsignada.rol,
            activo: true
          });
        } catch (personaError) {
          console.warn('Error asignando persona:', personaError);
        }
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/innovation');
      }, 2000);

    } catch (err) {
      console.error('Error creating initiative:', err);
      setError(err.response?.data?.error || 'Error al crear la iniciativa de innovación');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="alert alert-success text-center">
              <i className="fas fa-check-circle fa-3x mb-3"></i>
              <h4>¡Iniciativa de Innovación Creada Exitosamente!</h4>
              <p>Redirigiendo al dashboard de innovación...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-2">
            <i className="fas fa-plus-circle me-2" style={{color: '#28a745'}}></i>
            Nueva Iniciativa de Innovación
          </h1>
          <p className="text-muted mb-0">
            Crear una nueva iniciativa marcada como innovación
          </p>
        </div>
        <Link to="/innovation" className="btn btn-outline-secondary">
          <i className="fas fa-arrow-left me-2"></i>
          Volver a Innovación
        </Link>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Información Básica */}
          <div className="col-md-8">
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  Información Básica
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label htmlFor="nombre_de_la_oportunidad" className="form-label">
                      Título <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="nombre_de_la_oportunidad"
                      name="nombre_de_la_oportunidad"
                      value={formData.nombre_de_la_oportunidad}
                      onChange={handleInputChange}
                      placeholder="Título de la iniciativa de innovación"
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="tecnologia" className="form-label">
                      Área de Conocimiento
                    </label>
                    <select
                      className="form-control"
                      id="tecnologia"
                      name="tecnologia"
                      value={formData.tecnologia}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar área</option>
                      {Array.isArray(areas) && areas.map(area => (
                        <option key={area.id} value={area.id}>
                          {area.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="tipo_innovacion" className="form-label">
                      Tipo de Registro
                    </label>
                    <select
                      className="form-control"
                      id="tipo_innovacion"
                      name="tipo_innovacion"
                      value={formData.tipo_innovacion}
                      onChange={handleInputChange}
                    >
                      <option value="innocard">Innocard</option>
                      <option value="innotrans">Innotrans</option>
                    </select>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label htmlFor="descripcion" className="form-label">
                      Objetivo <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      id="descripcion"
                      name="descripcion"
                      rows="3"
                      value={formData.descripcion}
                      onChange={handleInputChange}
                      placeholder="Objetivo y descripción de la iniciativa"
                      required
                    />
                  </div>

                  <div className="col-md-12 mb-3">
                    <label htmlFor="relevancia_para_negocio" className="form-label">
                      Funcionalidad o Aplicación
                    </label>
                    <textarea
                      className="form-control"
                      id="relevancia_para_negocio"
                      name="relevancia_para_negocio"
                      rows="3"
                      value={formData.relevancia_para_negocio}
                      onChange={handleInputChange}
                      placeholder="Funcionalidad esperada y aplicación práctica"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Clasificación y Priorización */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-tags me-2"></i>
                  Clasificación y Priorización
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label htmlFor="probabilidad_de_aprobacion" className="form-label">
                      Prioridad
                    </label>
                    <select
                      className="form-control"
                      id="probabilidad_de_aprobacion"
                      name="probabilidad_de_aprobacion"
                      value={formData.probabilidad_de_aprobacion}
                      onChange={handleInputChange}
                    >
                      <option value="alta">Alta</option>
                      <option value="media">Media</option>
                      <option value="baja">Baja</option>
                    </select>
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="madurez" className="form-label">
                      Dificultad
                    </label>
                    <select
                      className="form-control"
                      id="madurez"
                      name="madurez"
                      value={formData.madurez}
                      onChange={handleInputChange}
                    >
                      <option value="poc">POC (Baja)</option>
                      <option value="piloto">Piloto (Media)</option>
                      <option value="proyecto">Proyecto (Alta)</option>
                    </select>
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="estado" className="form-label">
                      Estado Inicial
                    </label>
                    <select
                      className="form-control"
                      id="estado"
                      name="estado"
                      value={formData.estado}
                      onChange={handleInputChange}
                    >
                      <option value="oportunidad_identificada">Oportunidad Identificada</option>
                      <option value="en_preparacion_propuesta">En Preparación Propuesta</option>
                      <option value="supervision">Supervisión</option>
                    </select>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label htmlFor="linea_estrategica" className="form-label">
                      Línea Estratégica
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="linea_estrategica"
                      name="linea_estrategica"
                      value={formData.linea_estrategica}
                      onChange={handleInputChange}
                      placeholder="Línea estratégica de la organización"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Planificación Temporal */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-calendar me-2"></i>
                  Planificación Temporal
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="duracion_minima_estimada" className="form-label">
                      Duración Estimada Mínima (meses)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="duracion_minima_estimada"
                      name="duracion_minima_estimada"
                      value={formData.duracion_minima_estimada}
                      onChange={handleInputChange}
                      min="1"
                      max="60"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="duracion_maxima_estimada" className="form-label">
                      Duración Estimada Máxima (meses)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="duracion_maxima_estimada"
                      name="duracion_maxima_estimada"
                      value={formData.duracion_maxima_estimada}
                      onChange={handleInputChange}
                      min="1"
                      max="60"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="fecha_de_inicio_planificada" className="form-label">
                      Fecha de Inicio Estimada
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="fecha_de_inicio_planificada"
                      name="fecha_de_inicio_planificada"
                      value={formData.fecha_de_inicio_planificada}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="fecha_de_fin_planificada" className="form-label">
                      Fecha de Fin Estimada
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="fecha_de_fin_planificada"
                      name="fecha_de_fin_planificada"
                      value={formData.fecha_de_fin_planificada}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="fecha_de_inicio_real" className="form-label">
                      Fecha de Inicio Real
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="fecha_de_inicio_real"
                      name="fecha_de_inicio_real"
                      value={formData.fecha_de_inicio_real}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="fecha_de_entrega_propuesta_prevista" className="form-label">
                      Fecha de Entrega Propuesta <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="fecha_de_entrega_propuesta_prevista"
                      name="fecha_de_entrega_propuesta_prevista"
                      value={formData.fecha_de_entrega_propuesta_prevista}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Información Financiera */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-euro-sign me-2"></i>
                  Información Financiera
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="valor_minimo_estimado" className="form-label">
                      Presupuesto Estimado Mínimo (€)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="valor_minimo_estimado"
                      name="valor_minimo_estimado"
                      value={formData.valor_minimo_estimado}
                      onChange={handleInputChange}
                      min="0"
                      step="100"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="valor_maximo_estimado" className="form-label">
                      Presupuesto Estimado Máximo (€)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="valor_maximo_estimado"
                      name="valor_maximo_estimado"
                      value={formData.valor_maximo_estimado}
                      onChange={handleInputChange}
                      min="0"
                      step="100"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* KPIs y Enlaces */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-chart-bar me-2"></i>
                  KPIs y Enlaces
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label htmlFor="kpis_esperados" className="form-label">
                      KPI's Esperados
                    </label>
                    <textarea
                      className="form-control"
                      id="kpis_esperados"
                      name="kpis_esperados"
                      rows="3"
                      value={formData.kpis_esperados}
                      onChange={handleInputChange}
                      placeholder="KPIs e indicadores de éxito esperados"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="enlace_a_la_pagina_de_confluence" className="form-label">
                      Enlace Confluence
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="enlace_a_la_pagina_de_confluence"
                      name="enlace_a_la_pagina_de_confluence"
                      value={formData.enlace_a_la_pagina_de_confluence}
                      onChange={handleInputChange}
                      placeholder="https://confluence.ejemplo.com/..."
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="enlace_propuesta_pdf_sharepoint" className="form-label">
                      Enlace de SharePoint
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="enlace_propuesta_pdf_sharepoint"
                      name="enlace_propuesta_pdf_sharepoint"
                      value={formData.enlace_propuesta_pdf_sharepoint}
                      onChange={handleInputChange}
                      placeholder="https://sharepoint.ejemplo.com/..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Asignación de Personas */}
          <div className="col-md-4">
            <div className="card mb-4 sticky-top" style={{ top: '20px' }}>
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-users me-2"></i>
                  Asignación de Personas
                </h5>
              </div>
              <div className="card-body">
                <p className="text-muted small mb-3">
                  Selecciona las personas que trabajarán en esta iniciativa y sus roles.
                </p>
                
                {/* Combos para seleccionar persona y rol */}
                <div className="row mb-3">
                  <div className="col-md-5">
                    <label htmlFor="select-persona" className="form-label small">
                      Persona
                    </label>
                    <select
                      id="select-persona"
                      className="form-control form-control-sm"
                      value={selectedPersona}
                      onChange={(e) => setSelectedPersona(e.target.value)}
                    >
                      <option value="">Seleccionar persona</option>
                      {Array.isArray(personas) && personas.map(persona => (
                        <option key={persona.id} value={persona.id}>
                          {persona.nombre} {persona.apellido}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="col-md-5">
                    <label htmlFor="select-rol" className="form-label small">
                      Rol
                    </label>
                    <select
                      id="select-rol"
                      className="form-control form-control-sm"
                      value={selectedRol}
                      onChange={(e) => setSelectedRol(e.target.value)}
                    >
                      <option value="">Seleccionar rol</option>
                      <option value="jefe_proyecto">Líder de Proyecto</option>
                      <option value="lider_tecnico">Líder Técnico</option>
                      <option value="desarrollador">Desarrollador/Colaborador</option>
                    </select>
                  </div>
                  
                  <div className="col-md-2 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-success w-100"
                      onClick={addPersonaToList}
                      disabled={!selectedPersona || !selectedRol}
                      style={{ padding: '0.5rem' }}
                    >
                      <i className="fas fa-plus me-1"></i>
                      Añadir
                    </button>
                  </div>
                </div>

                {/* Lista de personas asignadas */}
                <div className="border rounded p-3" style={{ minHeight: '200px', maxHeight: '400px', overflowY: 'auto' }}>
                  <h6 className="mb-3">
                    <i className="fas fa-users me-2"></i>
                    Personas Asignadas ({formData.personas_asignadas.length})
                  </h6>
                  
                  {formData.personas_asignadas.length === 0 ? (
                    <p className="text-muted text-center mb-0">
                      No hay personas asignadas aún
                    </p>
                  ) : (
                    <div className="list-group">
                      {formData.personas_asignadas.map((asignacion, index) => {
                        const persona = getPersonaById(asignacion.persona);
                        return (
                          <div key={`${asignacion.persona}-${asignacion.rol}-${index}`} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2">
                            <div>
                              <div className="fw-bold text-dark">
                                {persona.nombre} {persona.apellido}
                              </div>
                              <small className="text-muted">
                                {getRolDisplay(asignacion.rol)}
                                {persona.area && (
                                  <span> • {persona.area}</span>
                                )}
                              </small>
                            </div>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => removePersonaFromList(asignacion.persona, asignacion.rol)}
                              title="Eliminar asignación"
                            >
                              <i className="fas fa-times me-1"></i>
                              Eliminar
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Botones de Acción */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center">
                <button
                  type="submit"
                  className="btn btn-success btn-lg me-3"
                  disabled={loading}
                  style={{ minWidth: '200px' }}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Creando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Crear Iniciativa de Innovación
                    </>
                  )}
                </button>
                
                <Link to="/innovation" className="btn btn-secondary btn-lg">
                  <i className="fas fa-times me-2"></i>
                  Cancelar
                </Link>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CrearIniciativaInnovacion;